/**
 * JIT Access — time-bound access to LLM targets.
 *
 * In the order an operator meets them:
 *   1. Requests waiting on a decision (filed by a user who hit a block). A user
 *      asks for just-in-time (time-bound) or full (ongoing) access; the
 *      approver decides which they actually get.
 *   2. Access that is live right now, with what is left on it.
 *   3. Full access granted from requests — standing until removed.
 *   4. The history, because "who had access to what, when" is the question
 *      asked after the fact.
 *
 * Every grant shows both a countdown and an absolute expiry timestamp. The
 * timestamp is what an approver actually agreed to; the countdown is only a
 * convenience, and showing it alone invites approving "a few hours" without
 * noticing that lands at 3am on a Saturday.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Icon, Modal } from '../components/ui';
import * as api from '../lib/targetAccessApi';
import type { EffectiveTarget, TargetGrant } from '../lib/targetAccessApi';
import { usePermission } from '../hooks/usePermission';
import { useTranslation } from 'react-i18next';
import { parseServerDate } from '../i18n/format';

interface Props {
  apiUrl: string;
}

interface Device {
  device_id: string;
  hostname?: string | null;
  os?: string | null;
}

interface JitRequest {
  id: string;
  kind: string;
  agent_id: string | null;
  requested_at: string;
  payload: {
    deviceId?: string;
    userUpn?: string;
    providerId?: string;
    host?: string;
    justification?: string;
    requestedTtlSecs?: number;
    accessType?: api.AccessType;
    /** Where the requester asked to be emailed the decision. */
    requesterEmail?: string | null;
    /** The requester as the synced Active Directory knows them. */
    requester?: { displayName: string | null; upn: string | null; disabled: boolean; groups: string[] } | null;
  };
}

const JitAccess: React.FC<Props> = ({ apiUrl }) => {
  const { t: tr } = useTranslation();
  const canWrite = usePermission('update:policy');

  const [devices, setDevices] = useState<Device[]>([]);
  const [grants, setGrants] = useState<TargetGrant[]>([]);
  const [requests, setRequests] = useState<JitRequest[]>([]);
  const [catalogue, setCatalogue] = useState<EffectiveTarget[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [decisionTtl, setDecisionTtl] = useState<Record<string, number>>({});
  const [decisionRouting, setDecisionRouting] = useState<Record<string, api.RoutingMode>>({});
  const [decisionType, setDecisionType] = useState<Record<string, api.AccessType>>({});
  const [standing, setStanding] = useState<api.StandingAccess[]>([]);

  // One shared clock for every countdown on the page — per-row timers drift
  // apart and repaint independently.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const deviceLabel = useCallback(
    (id: string) => {
      if (!id) return 'all endpoints';
      return devices.find((d) => d.device_id === id)?.hostname || id;
    },
    [devices]
  );

  const load = useCallback(async () => {
    setError(null);
    try {
      const [g, dev, appr, cat, st] = await Promise.all([
        api.listGrants(apiUrl, { includeExpired: true }),
        fetch(`${apiUrl}/admin/bdr/devices`).then((r) => r.json()).catch(() => ({ devices: [] })),
        fetch(`${apiUrl}/admin/approvals`).then((r) => r.json()).catch(() => ({ requests: [] })),
        api.listTargets(apiUrl, api.ORG_SCOPE).catch(() => ({ targets: [] as EffectiveTarget[] })),
        api.listStandingAccess(apiUrl).catch(() => [] as api.StandingAccess[]),
      ]);
      setStanding(st);
      setGrants(g.grants);
      setDevices(Array.isArray(dev.devices) ? dev.devices : []);
      setRequests((appr.requests || []).filter((r: JitRequest) => r.kind === 'jit_target_access'));
      setCatalogue((cat as any).targets || []);
    } catch (e: any) {
      setError(e.message || 'Could not load JIT access');
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    void load();
    // Poll so a request filed from an endpoint's block page shows up without a
    // manual refresh — same cadence as the agent approvals panel.
    const t = setInterval(() => void load(), 5000);
    return () => clearInterval(t);
  }, [load]);

  const mutate = async (key: string, fn: () => Promise<unknown>, msg: string) => {
    setBusy(key);
    setError(null);
    try {
      await fn();
      await load();
      setNote(msg);
      setTimeout(() => setNote(null), 4000);
    } catch (e: any) {
      setError(e.message || 'Action failed');
    } finally {
      setBusy(null);
    }
  };

  // Approve/deny asks for confirmation first, with a warning about what it does.
  const [confirming, setConfirming] = useState<{ r: JitRequest; decision: 'approve' | 'deny' } | null>(null);

  const decide = (r: JitRequest, decision: 'approve' | 'deny') => {
    const ttl = decisionTtl[r.id] ?? (r.payload.requestedTtlSecs || 3600);
    const routingMode = decisionRouting[r.id] ?? 'direct';
    const accessType = decisionType[r.id] ?? r.payload.accessType ?? 'jit';
    return mutate(
      r.id,
      async () => {
        const res = await fetch(`${apiUrl}/admin/approvals/${r.id}/decision`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(
            decision === 'approve' ? { decision, ttl_secs: ttl, routingMode, access_type: accessType } : { decision }
          ),
        });
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          throw new Error(d.message || d.error || `Failed to ${decision} the request`);
        }
      },
      decision === 'approve' ? (accessType === 'full' ? 'Full access granted.' : 'Access granted.') : 'Request denied.'
    );
  };

  const active = useMemo(() => grants.filter((g) => g.state === 'active' && g.expiresAt > now), [grants, now]);
  const past = useMemo(() => grants.filter((g) => !(g.state === 'active' && g.expiresAt > now)), [grants, now]);

  return (
    <div className="page-content">
      {confirming && (() => {
        const { r, decision } = confirming;
        const approve = decision === 'approve';
        const type = decisionType[r.id] ?? r.payload.accessType ?? 'jit';
        const who = r.payload.requester?.displayName || r.payload.userUpn;
        const close = () => setConfirming(null);
        return (
          <Modal
            title={approve ? 'Approve this access request?' : 'Deny this access request?'}
            onClose={close}
            maxWidth={480}
            footer={
              <>
                <button className="ent-btn" onClick={close}>Cancel</button>
                <button className={`ent-btn ${approve ? 'primary' : 'danger'}`} autoFocus
                  onClick={() => { close(); void decide(r, decision); }}>
                  {approve ? 'Yes, approve' : 'Yes, deny'}
                </button>
              </>
            }
          >
            <p style={{ marginTop: 0 }}>
              <strong>{who}</strong> asked for {type === 'full' ? 'full (ongoing)' : 'just-in-time'} access to <strong>{r.payload.providerId}</strong>.
            </p>
            {approve ? (
              <p style={{ color: 'var(--warn, #b7791f)' }}>
                {type === 'full'
                  ? 'Full access stays in place until an administrator removes it. The user can use this provider from now on.'
                  : 'The user can use this provider right away, until the access expires.'}
                {' '}This decision is recorded in the audit log{r.payload.requesterEmail ? ' and the user is emailed' : ''}.
              </p>
            ) : (
              <p>The user stays blocked from this provider.{r.payload.requesterEmail ? ' They are emailed the decision.' : ''} This is recorded in the audit log.</p>
            )}
          </Modal>
        );
      })()}
      <div className="page-title">
        <div>
          <h1>{tr('pages.jitAccess.jitAccess')}</h1>
          <p className="subtitle">
            {tr('pages.jitAccess.accessRequestsFromBlockedUsersAnd')} <strong>{tr('pages.jitAccess.llmAccessPolicy')}</strong>.
          </p>
        </div>
      </div>

      {error && (
        <div className="notice error">
          <Icon name="warning" /> {error}
        </div>
      )}
      {note && (
        <div className="notice">
          <Icon name="checkCircle" /> {note}
        </div>
      )}

      {/* 1. Waiting on a decision */}
      <div className="ent-card" style={{ marginBottom: 16 }}>
        <div className="ent-card-head">
          <Icon name="bell" /> {tr('pages.jitAccess.requestsAwaitingApproval')}
          {requests.length > 0 && <span className="badge badge-redact" style={{ marginLeft: 8 }}>{requests.length}</span>}
        </div>
        <div className="ent-card-body">
          {requests.length === 0 ? (
            <div className="ent-empty">
              {tr('pages.jitAccess.nothingWaitingRequestsAppearHereWhen')}
            </div>
          ) : (
            <div className="ent-table-wrap">
              <table className="ent-table">
                <thead>
                  <tr>
                    <th>{tr('pages.jitAccess.who')}</th>
                    <th>{tr('pages.jitAccess.service')}</th>
                    <th>{tr('pages.jitAccess.endpoint')}</th>
                    <th>{tr('pages.jitAccess.reason')}</th>
                    <th style={{ width: 340 }}>{tr('pages.jitAccess.decision')}</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map((r) => (
                    <tr key={r.id}>
                      <td>
                        <strong>
                          {r.payload.requester?.displayName ||
                            (r.payload.userUpn === api.ALL_USERS ? tr('pages.jitAccess.unidentifiedUser') : r.payload.userUpn)}
                        </strong>
                        {r.payload.requester && (
                          <div style={{ fontSize: 10, color: 'var(--text-2)' }}>
                            {r.payload.requester.upn || r.payload.userUpn}
                            {r.payload.requester.disabled && (
                              <span className="badge badge-error" style={{ marginLeft: 4, fontSize: 9 }}>{tr('pages.jitAccess.disabledInAD')}</span>
                            )}
                          </div>
                        )}
                        {r.payload.requester?.groups?.length ? (
                          <div style={{ fontSize: 10, color: 'var(--text-2)', maxWidth: 200 }} title={r.payload.requester.groups.join(', ')}>
                            {r.payload.requester.groups.slice(0, 3).join(', ')}
                            {r.payload.requester.groups.length > 3 ? ` +${r.payload.requester.groups.length - 3}` : ''}
                          </div>
                        ) : null}
                        <div style={{ fontSize: 10, color: 'var(--text-2)', marginTop: 2 }}>
                          {parseServerDate(r.requested_at).toLocaleString()}
                        </div>
                      </td>
                      <td>
                        {r.payload.providerId}
                        {r.payload.host && (
                          <div style={{ fontSize: 10, color: 'var(--text-2)', fontFamily: 'monospace' }}>
                            {r.payload.host}
                          </div>
                        )}
                      </td>
                      <td style={{ fontSize: 12 }}>{deviceLabel(r.payload.deviceId || '')}</td>
                      <td style={{ fontSize: 12, maxWidth: 260 }}>
                        {r.payload.justification || <span style={{ color: 'var(--text-2)' }}>—</span>}
                        <div style={{ marginTop: 4 }}>
                          {r.payload.accessType === 'full' ? (
                            <span className="badge badge-redact" style={{ fontSize: 9 }}>{tr('pages.jitAccess.askedForFullAccess')}</span>
                          ) : (
                            <span className="badge badge-info" style={{ fontSize: 9 }}>
                              {tr('pages.jitAccess.askedForJit')}
                              {r.payload.requestedTtlSecs ? ` · ${api.remainingLabel(r.payload.requestedTtlSecs * 1000)}` : ''}
                            </span>
                          )}
                        </div>
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {/* The approver's choice is what applies; the request's
                            ask only seeds these controls. */}
                        <select
                          className="ent-input"
                          style={{ width: 78, fontSize: 12, display: 'inline-block', marginRight: 6 }}
                          value={decisionType[r.id] ?? r.payload.accessType ?? 'jit'}
                          disabled={!canWrite}
                          title={tr('pages.jitAccess.justInTimeEndsByItself')}
                          onChange={(e) => setDecisionType((m) => ({ ...m, [r.id]: e.target.value as api.AccessType }))}
                        >
                          <option value="jit">{tr('pages.jitAccess.jit')}</option>
                          <option value="full">{tr('pages.jitAccess.full')}</option>
                        </select>
                        <select
                          className="ent-input"
                          style={{ width: 96, fontSize: 12, display: 'inline-block', marginRight: 6 }}
                          value={decisionTtl[r.id] ?? (r.payload.requestedTtlSecs || 3600)}
                          disabled={!canWrite || (decisionType[r.id] ?? r.payload.accessType ?? 'jit') === 'full'}
                          onChange={(e) => setDecisionTtl((m) => ({ ...m, [r.id]: Number(e.target.value) }))}
                        >
                          {api.DURATION_PRESETS.map((d) => (
                            <option key={d.secs} value={d.secs}>
                              {tr(d.labelKey)}
                            </option>
                          ))}
                        </select>
                        <select
                          className="ent-input"
                          style={{ width: 84, fontSize: 12, display: 'inline-block', marginRight: 6 }}
                          value={decisionRouting[r.id] ?? 'direct'}
                          disabled={!canWrite}
                          title={tr('pages.jitAccess.howTheApprovedTrafficReachesThe')}
                          onChange={(e) =>
                            setDecisionRouting((m) => ({ ...m, [r.id]: e.target.value as api.RoutingMode }))
                          }
                        >
                          <option value="direct">{tr('pages.jitAccess.direct')}</option>
                          <option value="tunnel">{tr('pages.jitAccess.tunnel')}</option>
                        </select>
                        <button
                          className="ent-btn sm primary"
                          disabled={!canWrite || busy === r.id}
                          onClick={() => setConfirming({ r, decision: 'approve' })}
                        >
                          <Icon name="checkCircle" /> {tr('pages.jitAccess.approve')}
                        </button>
                        <button
                          className="ent-btn sm"
                          style={{ marginLeft: 4 }}
                          disabled={!canWrite || busy === r.id}
                          onClick={() => setConfirming({ r, decision: 'deny' })}
                        >
                          <Icon name="deny" /> {tr('pages.jitAccess.deny')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* 2. Grant directly */}
      {canWrite && (
        <GrantForm
          apiUrl={apiUrl}
          devices={devices}
          catalogue={catalogue}
          busy={busy === 'new'}
          onGrant={(fn) => mutate('new', fn, 'Access granted.')}
        />
      )}

      {/* 3. Live now */}
      <div className="ent-card" style={{ marginBottom: 16 }}>
        <div className="ent-card-head">
          <Icon name="clock" /> {tr('pages.jitAccess.activeAccess')}
          {active.length > 0 && <span className="badge badge-success" style={{ marginLeft: 8 }}>{active.length}</span>}
        </div>
        <div className="ent-card-body">
          {loading ? (
            tr('pages.jitAccess.loading')
          ) : active.length === 0 ? (
            <div className="ent-empty">{tr('pages.jitAccess.noTimeBoundAccessIsLive')}</div>
          ) : (
            <div className="ent-table-wrap">
              <table className="ent-table">
                <thead>
                  <tr>
                    <th>{tr('pages.jitAccess.service')}</th>
                    <th>{tr('pages.jitAccess.who')}</th>
                    <th>{tr('pages.jitAccess.endpoint')}</th>
                    <th>{tr('pages.jitAccess.expires')}</th>
                    <th>{tr('pages.jitAccess.reason')}</th>
                    {canWrite && <th style={{ width: 200 }}>{tr('pages.jitAccess.actions')}</th>}
                  </tr>
                </thead>
                <tbody>
                  {active.map((g) => {
                    const remaining = g.expiresAt - now;
                    return (
                      <tr key={g.id}>
                        <td>
                          <strong>{g.providerLabel}</strong>
                          {g.routingMode === 'tunnel' && (
                            <span className="badge badge-info" style={{ marginLeft: 6, fontSize: 9 }}>
                              {tr('pages.jitAccess.tunnelled')}
                            </span>
                          )}
                          {g.source === 'request' && (
                            <div style={{ fontSize: 10, color: 'var(--text-2)' }}>{tr('pages.jitAccess.fromARequest')}</div>
                          )}
                          {g.extendedFrom && (
                            <div style={{ fontSize: 10, color: 'var(--text-2)' }}>{tr('pages.jitAccess.extended')}</div>
                          )}
                        </td>
                        <td style={{ fontSize: 12 }}>
                          {g.userUpn === api.ALL_USERS ? (
                            <span style={{ color: 'var(--text-2)' }}>{tr('pages.jitAccess.everyone')}</span>
                          ) : (
                            g.userUpn
                          )}
                        </td>
                        <td style={{ fontSize: 12 }}>{deviceLabel(g.deviceId)}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <span className={`badge ${remaining < 300_000 ? 'badge-redact' : 'badge-success'}`}>
                            {api.remainingLabel(remaining)}
                          </span>
                          <div style={{ fontSize: 10, color: 'var(--text-2)', marginTop: 2 }}>
                            {parseServerDate(g.expiresAt).toLocaleString()}
                          </div>
                        </td>
                        <td style={{ fontSize: 12, maxWidth: 220 }}>
                          {g.reason || <span style={{ color: 'var(--text-2)' }}>—</span>}
                          <div style={{ fontSize: 10, color: 'var(--text-2)' }}>{tr('pages.jitAccess.by', { v0: g.grantedBy })}</div>
                        </td>
                        {canWrite && (
                          <td style={{ whiteSpace: 'nowrap' }}>
                            <button
                              className="ent-btn sm"
                              disabled={busy === g.id}
                              onClick={() => void mutate(g.id, () => api.extendGrant(apiUrl, g.id, 3600_000), 'Extended by 1 hour.')}
                            >
                              {tr('pages.jitAccess.1h')}
                            </button>
                            <button
                              className="ent-btn sm"
                              style={{ marginLeft: 4 }}
                              disabled={busy === g.id}
                              onClick={() => void mutate(g.id, () => api.extendGrant(apiUrl, g.id, 86_400_000), 'Extended by 1 day.')}
                            >
                              {tr('pages.jitAccess.1d')}
                            </button>
                            <button
                              className="ent-btn sm"
                              style={{ marginLeft: 4 }}
                              disabled={busy === g.id}
                              title={tr('pages.jitAccess.endThisAccessNow')}
                              onClick={() => void mutate(g.id, () => api.revokeGrant(apiUrl, g.id), 'Access revoked.')}
                            >
                              <Icon name="deny" /> {tr('pages.jitAccess.revoke')}
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* 3b. Full access granted from requests */}
      <div className="ent-card" style={{ marginBottom: 16 }}>
        <div className="ent-card-head">
          <Icon name="shield" /> {tr('pages.jitAccess.fullAccessGranted')}
          {standing.length > 0 && <span className="badge badge-info" style={{ marginLeft: 8 }}>{standing.length}</span>}
        </div>
        <div className="ent-card-body">
          {standing.length === 0 ? (
            <div className="ent-empty">{tr('pages.jitAccess.noOngoingAccessHasBeenGranted')}</div>
          ) : (
            <div className="ent-table-wrap">
              <table className="ent-table">
                <thead>
                  <tr>
                    <th>{tr('pages.jitAccess.service')}</th>
                    <th>{tr('pages.jitAccess.who')}</th>
                    <th>{tr('pages.jitAccess.granted')}</th>
                    {canWrite && <th style={{ width: 120 }}>{tr('pages.jitAccess.actions')}</th>}
                  </tr>
                </thead>
                <tbody>
                  {standing.map((s) => {
                    const key = `standing:${s.userUpn}:${s.providerId}`;
                    return (
                      <tr key={key}>
                        <td>
                          <strong>{s.label}</strong>
                          {s.routingMode === 'tunnel' && (
                            <span className="badge badge-info" style={{ marginLeft: 6, fontSize: 9 }}>{tr('pages.jitAccess.tunnelled')}</span>
                          )}
                        </td>
                        <td style={{ fontSize: 12 }}>
                          {s.userUpn}
                          <div style={{ fontSize: 10, color: 'var(--text-2)' }}>{tr('pages.jitAccess.allOfTheirEndpoints')}</div>
                        </td>
                        <td style={{ fontSize: 11 }}>
                          {parseServerDate(s.updatedAt).toLocaleString()}
                          {s.updatedBy && <div style={{ fontSize: 10, color: 'var(--text-2)' }}>{tr('pages.jitAccess.by', { v0: s.updatedBy })}</div>}
                        </td>
                        {canWrite && (
                          <td>
                            <button
                              className="ent-btn sm"
                              disabled={busy === key}
                              title={tr('pages.jitAccess.removeThisOngoingAccess')}
                              onClick={() =>
                                void mutate(key, () => api.removeStandingAccess(apiUrl, s.userUpn, s.providerId), 'Full access removed.')
                              }
                            >
                              <Icon name="deny" /> {tr('pages.jitAccess.remove')}
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* 4. After the fact */}
      <div className="ent-card">
        <div className="ent-card-head" style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>
            <Icon name="logs" /> {tr('pages.jitAccess.history')}
          </span>
          <button className="ent-btn sm" onClick={() => setShowHistory((v) => !v)}>
            {showHistory ? tr('pages.jitAccess.hide') : `Show (${past.length})`}
          </button>
        </div>
        {showHistory && (
          <div className="ent-card-body">
            {past.length === 0 ? (
              <div className="ent-empty">{tr('pages.jitAccess.nothingHasExpiredOrBeenRevoked')}</div>
            ) : (
              <div className="ent-table-wrap scroll">
                <table className="ent-table">
                  <thead>
                    <tr>
                      <th>{tr('pages.jitAccess.service')}</th>
                      <th>{tr('pages.jitAccess.who')}</th>
                      <th>{tr('pages.jitAccess.endpoint')}</th>
                      <th>{tr('pages.jitAccess.granted')}</th>
                      <th>{tr('pages.jitAccess.ended')}</th>
                      <th>{tr('pages.jitAccess.outcome')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {past.map((g) => (
                      <tr key={g.id}>
                        <td>{g.providerLabel}</td>
                        <td style={{ fontSize: 12 }}>{g.userUpn === api.ALL_USERS ? tr('pages.jitAccess.everyone') : g.userUpn}</td>
                        <td style={{ fontSize: 12 }}>{deviceLabel(g.deviceId)}</td>
                        <td style={{ fontSize: 11 }}>{parseServerDate(g.grantedAt).toLocaleString()}</td>
                        <td style={{ fontSize: 11 }}>
                          {parseServerDate(g.revokedAt ?? g.expiresAt).toLocaleString()}
                        </td>
                        <td>
                          <span className={`badge ${g.state === 'revoked' ? 'badge-error' : 'badge-unknown'}`}>
                            {g.state === 'revoked' ? `revoked by ${g.revokedBy}` : tr('pages.jitAccess.expired')}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/* ---- grant directly, without a request ------------------------------------ */

const GrantForm: React.FC<{
  apiUrl: string;
  devices: Device[];
  catalogue: EffectiveTarget[];
  busy: boolean;
  onGrant: (fn: () => Promise<unknown>) => Promise<void>;
}> = ({ apiUrl, devices, catalogue, busy, onGrant }) => {
  const { t: tr } = useTranslation();
  const [open, setOpen] = useState(false);
  const [deviceId, setDeviceId] = useState('');
  const [providerId, setProviderId] = useState('');
  const [userUpn, setUserUpn] = useState('');
  const [ttlSecs, setTtlSecs] = useState(3600);
  const [reason, setReason] = useState('');
  const [routingMode, setRoutingMode] = useState<api.RoutingMode>('direct');

  const submit = async () => {
    if (!providerId) return;
    await onGrant(() =>
      api.createGrant(apiUrl, {
        deviceId,
        userUpn: userUpn.trim() || undefined,
        providerId,
        ttlSecs,
        reason: reason.trim() || undefined,
        routingMode,
      })
    );
    setProviderId('');
    setUserUpn('');
    setReason('');
    setRoutingMode('direct');
    setOpen(false);
  };

  return (
    <div className="ent-card" style={{ marginBottom: 16 }}>
      <div className="ent-card-head" style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span>
          <Icon name="plus" /> {tr('pages.jitAccess.grantAccessDirectly')}
        </span>
        <button className="ent-btn sm" onClick={() => setOpen((v) => !v)}>
          {open ? tr('pages.jitAccess.cancel') : tr('pages.jitAccess.newGrant')}
        </button>
      </div>
      {open && (
        <div className="ent-card-body">
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label style={{ display: 'grid', gap: 4, fontSize: 12 }}>
              <span style={{ color: 'var(--text-2)' }}>{tr('pages.jitAccess.endpoint')}</span>
              <select className="ent-input" style={{ minWidth: 220 }} value={deviceId} onChange={(e) => setDeviceId(e.target.value)}>
                <option value="">{tr('pages.jitAccess.allEndpoints')}</option>
                {devices.map((d) => (
                  <option key={d.device_id} value={d.device_id}>
                    {d.hostname || d.device_id}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ display: 'grid', gap: 4, fontSize: 12 }}>
              <span style={{ color: 'var(--text-2)' }}>{tr('pages.jitAccess.service')}</span>
              <select className="ent-input" style={{ minWidth: 180 }} value={providerId} onChange={(e) => setProviderId(e.target.value)}>
                <option value="">{tr('pages.jitAccess.choose')}</option>
                {catalogue.map((t) => (
                  <option key={t.providerId} value={t.providerId}>
                    {t.label}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ display: 'grid', gap: 4, fontSize: 12 }}>
              <span style={{ color: 'var(--text-2)' }}>{tr('pages.jitAccess.userBlankEveryone')}</span>
              <input className="ent-input" placeholder={tr('pages.jitAccess.aliceCorpCom')} value={userUpn} onChange={(e) => setUserUpn(e.target.value)} />
            </label>
            <label style={{ display: 'grid', gap: 4, fontSize: 12 }}>
              <span style={{ color: 'var(--text-2)' }}>{tr('pages.jitAccess.forHowLong')}</span>
              <select className="ent-input" value={ttlSecs} onChange={(e) => setTtlSecs(Number(e.target.value))}>
                {api.DURATION_PRESETS.map((d) => (
                  <option key={d.secs} value={d.secs}>
                    {tr(d.labelKey)}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ display: 'grid', gap: 4, fontSize: 12 }}>
              <span style={{ color: 'var(--text-2)' }}>{tr('pages.jitAccess.routeVia')}</span>
              <select
                className="ent-input"
                value={routingMode}
                onChange={(e) => setRoutingMode(e.target.value as api.RoutingMode)}
                title={tr('pages.jitAccess.directEndpointMITMProxyForwardsStraight')}
              >
                <option value="direct">{tr('pages.jitAccess.directMITM')}</option>
                <option value="tunnel">{tr('pages.jitAccess.tunnelViaAgenti5')}</option>
              </select>
            </label>
            <label style={{ display: 'grid', gap: 4, fontSize: 12, flex: 1, minWidth: 200 }}>
              <span style={{ color: 'var(--text-2)' }}>{tr('pages.jitAccess.reason')}</span>
              <input className="ent-input" placeholder={tr('pages.jitAccess.whyThisAccessIsNeeded')} value={reason} onChange={(e) => setReason(e.target.value)} />
            </label>
            <button className="ent-btn primary" disabled={busy || !providerId} onClick={() => void submit()}>
              <Icon name="check" /> {tr('pages.jitAccess.grant')}
            </button>
          </div>
          {/* Show the date being approved, not just the duration. */}
          <p className="subtitle" style={{ marginBottom: 0, marginTop: 8 }}>{tr('pages.jitAccess.expires2', { v0: parseServerDate(Date.now() + ttlSecs * 1000).toLocaleString() })}</p>
        </div>
      )}
    </div>
  );
};

export default JitAccess;
